import { orderBy } from 'lodash';
import { useQuery } from 'react-query';
import { LiveFeedEventResult, Sort } from '../domain';
import { apiHttpService } from './apiHttpService';
import { useEventFeed } from './useEventFeed';

export function useEventResults(
  eventId?: string | null,
  classId?: string | null,
  searchQuery?: string,
  sort?: Sort,
) {
  const { data: activeEvent } = useEventFeed({
    eventId,
  });
  return useQuery<LiveFeedEventResult[]>(
    ['eventResults', eventId, classId],
    async () => {
      const { data } = await apiHttpService.get<{
        results: LiveFeedEventResult[];
      }>(`/GetEventResults?eventId=${eventId}&classId=${classId}`);

      return data.results;
    },
    {
      enabled: !!eventId && !!classId,
      staleTime: Infinity,
      // results are refetched after updating event (check useDidUpdateEffect in Results.container)
      // and here (by given autoRefreshResultsFeedTime from Event)
      refetchInterval: () => {
        if (activeEvent?.toDatePassed) {
          return false;
        }
        if (activeEvent) {
          const [hours, minutes, seconds] =
            activeEvent.autoRefreshResultsFeedTime.split(':');
          const intervalTimeMs =
            Number(hours) * 3.6e6 +
            Number(minutes) * 6e4 +
            Number(seconds) * 1000;
          if (intervalTimeMs) {
            return intervalTimeMs;
          }
        }
        return false;
      },
      select: (data) => {
        let processedData = searchQuery
          ? data.filter(
              (item) =>
                item.riderBSJARegNumber
                  ?.toLowerCase()
                  .includes(searchQuery.toLowerCase()) ||
                item.riderFirstName
                  .toLowerCase()
                  .includes(searchQuery.toLowerCase()) ||
                item.riderLastName
                  .toLowerCase()
                  .includes(searchQuery.toLowerCase()) ||
                item.customHorseName
                  ?.toLowerCase()
                  .includes(searchQuery.toLowerCase()) ||
                item.horseName
                  ?.toLowerCase()
                  .includes(searchQuery.toLowerCase()) ||
                item.bridleNumber
                  .toString()
                  .includes(searchQuery.toLowerCase()),
            )
          : data;

        processedData = sort
          ? orderBy(processedData, sort.field, sort.order)
          : orderBy(processedData, 'sjPlace', 'asc');


        if (sort?.field === 'sjPlace' || sort?.field === 'overallPlace') {
          const zeroValuesItems = processedData.filter(
            (item) =>
              !item[(sort?.field || 'sjPlace') as keyof LiveFeedEventResult],
          );
  
          const withoutZeroValues = processedData.filter(
            (item) =>
              item[(sort?.field || 'sjPlace') as keyof LiveFeedEventResult],
          );

          if (sort?.order === 'asc') {
            // putting zero values items to the end
            processedData = withoutZeroValues.concat(zeroValuesItems);   
          }
  
          if (sort?.order === 'desc') {
            // putting zero values items to the start
            processedData = zeroValuesItems.concat(withoutZeroValues);
          }
        }


        return processedData;
      },
    },
  );
}
